import { startCase } from 'lodash'
import type { List } from 'app/generated/graphql'
import type { CustomPages } from 'app/types/entities'
import type { ListVisibility } from 'app/types/list-visibility'
import type {
  NavGate,
  NavItemsInput,
  NavListEntry,
  NavListGroup,
  NavListsSetting,
  NavPreset,
  RecordTypeDef,
} from 'app/types/navigation'
import { filterListPath, isManualListPipeline, manualListPath } from 'lib/lists'
import { isPersonalList } from 'lib/listVisibility'
import { isNavItemVisible } from 'lib/navVisibility'
import { isFilterList, isPracticeList } from 'lib/practiceLists'
import { RECORD_TYPES, customListItem, recordTypeRow } from 'lib/recordTypes'
import { RETIRED_CONTACT_PRESETS, SYSTEM_LISTS, systemListItem } from 'lib/systemLists'
import type { NavItemType } from 'types/Sidebar'

export const VISIBLE_LIST_LIMIT = 3

export const EMPTY_NAV_LISTS_SETTING: Readonly<NavListsSetting> = { order: [], hidden: [], showAll: false }

/** Custom list keys belong to one workspace, so each workspace keeps its own order. */
export function navListsSettingKey(workspaceId: string | undefined): string {
  return `navLists-${workspaceId ?? `org`}`
}

/**
 * What a manual list is gated on. Shaped like `RecordTypeDef.gate` because it answers the same
 * question in two places: whether to draw a list that exists, and whether to offer making one.
 */
export function manualListGates(input: NavItemsInput): Pick<NavItemType, `if` | `product` | `permission`> {
  return {
    if: !!input.user.workspaceId,
    product: `CRM`,
    permission: [`org:pages:pipelines`, `pages:pipelines`],
  }
}

const isSystem = (entry: NavListEntry) => entry.source === `system`

/**
 * Lists draws Trackable's built-in lists first and the practice's own after them, in one group:
 * filter and manual lists mix in whatever order the person saves. A list's kind is said on its page.
 */
export const NAV_LIST_GROUP: Readonly<Record<NavListEntry[`source`], NavListGroup>> = {
  system: `builtIn`,
  preset: `practice`,
  custom: `practice`,
  manual: `practice`,
}

const NAV_LIST_GROUP_ORDER: readonly NavListGroup[] = [`builtIn`, `practice`]

/**
 * Who sees a sidebar list. A saved custom page belongs to the user who saved it, so it is
 * personal; live lists, presets and pipelines are the same for the whole team.
 */
export function navListVisibility(entry: NavListEntry): ListVisibility {
  if (entry.list) return isPersonalList(entry.list) ? `personal` : `shared`
  return entry.customPage ? `personal` : `shared`
}

/** Activity outcomes stay Activities page filters; they can be saved as custom lists instead. */
function presetSurfaced(recordType: RecordTypeDef, preset: NavPreset): boolean {
  if (recordType.key === `contacts`) return !RETIRED_CONTACT_PRESETS.has(preset.slug)
  return recordType.key !== `activities`
}

/**
 * The record types Lists draws from and offers for a new list. Tasks moved to its own
 * accordion under Work (see navSections.tasksGroup), so it never contributes here.
 */
export function listRecordTypes(input: NavItemsInput, gate: NavGate): CustomPages[] {
  return Object.values(RECORD_TYPES)
    .filter(
      (recordType): recordType is RecordTypeDef & { customPageType: CustomPages } =>
        !!recordType.customPageType && recordType.key !== `tasks`,
    )
    .filter((recordType) => isNavItemVisible(recordTypeRow(recordType, input), gate))
    .map((recordType) => recordType.customPageType)
}

export function buildNavListEntries(input: NavItemsInput, gate: NavGate): NavListEntry[] {
  const systems = SYSTEM_LISTS.map((def) => ({
    key: `system:${def.slug}`,
    recordType: def.recordType,
    item: systemListItem(def, input),
    source: `system` as const,
    def,
  })).filter((entry) => isNavItemVisible(entry.item, gate))
  const recordTypes = listRecordTypes(input, gate).map((key) => RECORD_TYPES[key])
  const presets = recordTypes.flatMap((recordType) =>
    recordType
      .presets(input)
      .filter((preset) => isNavItemVisible(preset, gate))
      .filter((preset) => presetSurfaced(recordType, preset))
      .map((preset) => ({
        key: `preset:${recordType.key}:${preset.slug}`,
        recordType: recordType.key,
        item: preset,
        source: `preset` as const,
      })),
  )
  const practiceLists = input.lists?.filter(isPracticeList)
  const customs = recordTypes.flatMap((recordType) =>
    // With Lists present, the contacts saved lists come from them below; the other record
    // types keep their custom pages.
    (recordType.customPageType && !(practiceLists && recordType.key === `contacts`)
      ? input.customPages[recordType.customPageType]
      : []
    ).map((page) => {
      const item = customListItem(recordType, page, input)
      return {
        key: `custom:${page.id}`,
        recordType: recordType.key,
        // A contacts custom page is a filter list with its own page now; activities and
        // tasks custom pages stay filters on their shared record page.
        item:
          recordType.key === `contacts`
            ? { ...item, href: filterListPath(page.id!), active: input.location.pathname === filterListPath(page.id!) }
            : item,
        source: `custom` as const,
        customPage: page,
        list: input.lists?.find((list) => list.id === page.id),
      }
    }),
  )
  if (practiceLists) {
    // A filter list is gated like the contacts record page it filters; a manual list on pipelines.
    const filters = recordTypes.some((recordType) => recordType.key === `contacts`)
      ? practiceLists.filter(isFilterList).map((list) => ({
          key: `custom:${list.id}`,
          recordType: `contacts` as const,
          source: `custom` as const,
          list,
          // Saved lists have always been shown title-cased, a rule the old custom page rows set.
          item: { ...listRowItem(list, filterListPath(list.id), input), name: startCase(list.name) || `Untitled list` },
        }))
      : []
    const manuals = practiceLists
      .filter((list) => !isFilterList(list))
      .map((list) => ({
        key: `manual:${list.id}`,
        recordType: `contacts` as const,
        source: `manual` as const,
        list,
        item: { ...listRowItem(list, manualListPath(list.id), input), ...manualListGates(input) },
      }))
      .filter((entry) => isNavItemVisible(entry.item, gate))
    return [...systems, ...presets, ...customs, ...filters, ...manuals]
  }
  const manuals = input.pipelines
    .filter(isManualListPipeline)
    .map((pipeline) => ({
      key: `manual:${pipeline.id}`,
      recordType: `contacts` as const,
      source: `manual` as const,
      pipeline,
      item: {
        name: pipeline.name ?? `Untitled list`,
        href: manualListPath(pipeline.id!),
        ...manualListGates(input),
        active: input.location.pathname === manualListPath(pipeline.id!),
        count: input.counts.pipelines?.[pipeline.id!],
      },
    }))
    .filter((entry) => isNavItemVisible(entry.item, gate))
  return [...systems, ...presets, ...customs, ...manuals]
}

function listRowItem(list: List, href: string, input: NavItemsInput): NavItemType {
  return {
    name: list.name || `Untitled list`,
    href,
    active: input.location.pathname === href,
    count: input.counts.lists?.[list.id],
  }
}

function orderGroup(entries: NavListEntry[], order: string[]): NavListEntry[] {
  const byKey = new Map(entries.map((entry) => [entry.key, entry]))
  const saved = order.flatMap((key) => byKey.get(key) ?? [])
  const savedKeys = new Set(saved.map((entry) => entry.key))
  return [...saved, ...entries.filter((entry) => !savedKeys.has(entry.key))]
}

/** Built-in lists, then the practice's own; a saved order only moves an entry within its own group. */
export function orderNavLists(entries: NavListEntry[], setting: NavListsSetting): NavListEntry[] {
  return NAV_LIST_GROUP_ORDER.flatMap((group) =>
    orderGroup(
      entries.filter((entry) => NAV_LIST_GROUP[entry.source] === group),
      setting.order,
    ),
  )
}

export function visibleNavLists(
  entries: NavListEntry[],
  setting: NavListsSetting,
): { visible: NavListEntry[]; overflowCount: number; canCollapse: boolean } {
  const hidden = new Set(setting.hidden)
  const shown = orderNavLists(entries, setting).filter((entry) => !hidden.has(entry.key))
  const pinned = shown.filter(isSystem)
  const rest = shown.filter((entry) => !isSystem(entry))
  if (rest.length <= VISIBLE_LIST_LIMIT) return { visible: shown, overflowCount: 0, canCollapse: false }
  if (setting.showAll) return { visible: shown, overflowCount: 0, canCollapse: true }
  const activeBeyondLimit = rest.slice(VISIBLE_LIST_LIMIT).find((entry) => entry.item.active)
  const head = rest.slice(0, VISIBLE_LIST_LIMIT)
  return {
    visible: activeBeyondLimit ? [...pinned, ...head, activeBeyondLimit] : [...pinned, ...head],
    overflowCount: rest.length - VISIBLE_LIST_LIMIT - (activeBeyondLimit ? 1 : 0),
    canCollapse: false,
  }
}

export function pruneNavListsSetting(setting: NavListsSetting, entries: NavListEntry[]): NavListsSetting {
  const keys = new Set(entries.map((entry) => entry.key))
  return {
    ...setting,
    order: setting.order.filter((key) => keys.has(key)),
    hidden: setting.hidden.filter((key) => keys.has(key)),
  }
}

export function moveNavListKey(keys: string[], draggedKey: string, targetKey: string): string[] {
  const targetIndex = keys.indexOf(targetKey)
  if (draggedKey === targetKey || targetIndex === -1 || !keys.includes(draggedKey)) return keys
  const next = keys.filter((key) => key !== draggedKey)
  next.splice(targetIndex, 0, draggedKey)
  return next
}
